import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const VariablePicker = ({ notificationType, onSelect, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  const variablesByType = {
    assignment: [
      { variable: '{{reviewerName}}', description: 'Reviewer\'s full name' },
      { variable: '{{paperTitle}}', description: 'Title of the paper' },
      { variable: '{{paperAuthors}}', description: 'List of paper authors' },
      { variable: '{{deadline}}', description: 'Review deadline date' },
      { variable: '{{conferenceName}}', description: 'Conference name' },
      { variable: '{{reviewLink}}', description: 'Link to review interface' },
      { variable: '{{paperAbstract}}', description: 'Paper abstract' },
      { variable: '{{submissionId}}', description: 'Submission ID number' }
    ],
    decision: [
      { variable: '{{authorName}}', description: 'Author\'s full name' },
      { variable: '{{paperTitle}}', description: 'Title of the paper' },
      { variable: '{{decision}}', description: 'Accept/Reject decision' },
      { variable: '{{reviewerComments}}', description: 'Aggregated reviewer feedback' },
      { variable: '{{conferenceName}}', description: 'Conference name' },
      { variable: '{{decisionDate}}', description: 'Date of decision' },
      { variable: '{{submissionId}}', description: 'Submission ID number' },
      { variable: '{{nextSteps}}', description: 'Next steps for authors' }
    ],
    deadline_reminder: [
      { variable: '{{recipientName}}', description: 'Recipient\'s full name' },
      { variable: '{{deadline}}', description: 'Upcoming deadline date' },
      { variable: '{{deadlineType}}', description: 'Type of deadline (submission, review, etc.)' },
      { variable: '{{daysRemaining}}', description: 'Days until deadline' },
      { variable: '{{conferenceName}}', description: 'Conference name' },
      { variable: '{{actionRequired}}', description: 'Required action description' },
      { variable: '{{actionLink}}', description: 'Link to take action' }
    ],
    status_update: [
      { variable: '{{recipientName}}', description: 'Recipient\'s full name' },
      { variable: '{{paperTitle}}', description: 'Title of the paper' },
      { variable: '{{currentStatus}}', description: 'Current submission status' },
      { variable: '{{statusDate}}', description: 'Date of status change' },
      { variable: '{{conferenceName}}', description: 'Conference name' },
      { variable: '{{submissionId}}', description: 'Submission ID number' },
      { variable: '{{additionalInfo}}', description: 'Additional status information' }
    ],
    custom: [
      { variable: '{{recipientName}}', description: 'Recipient\'s full name' },
      { variable: '{{conferenceName}}', description: 'Conference name' },
      { variable: '{{conferenceDate}}', description: 'Conference date' },
      { variable: '{{conferenceLocation}}', description: 'Conference location' },
      { variable: '{{organizerName}}', description: 'Organizer\'s name' },
      { variable: '{{organizerEmail}}', description: 'Organizer\'s email' },
      { variable: '{{websiteLink}}', description: 'Conference website link' }
    ]
  };

  const availableVariables = variablesByType?.[notificationType] || variablesByType?.custom;

  const handleVariableClick = (variable) => {
    onSelect?.(variable);
    if (compact) {
      setIsOpen(false);
    }
  };

  if (compact) {
    return (
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Icon name="Code" size={14} className="mr-1" />
          Insert Variable
        </Button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute end-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-auto">
              <div className="p-3 border-b border-border">
                <h4 className="text-sm font-semibold text-foreground">Available Variables</h4>
                <p className="text-xs text-muted-foreground mt-1">Click to insert at cursor position</p>
              </div>
              <div className="p-2">
                {availableVariables?.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleVariableClick(item?.variable)}
                    className="w-full text-left px-3 py-2 hover:bg-muted transition-colors"
                  >
                    <code className="text-sm font-mono text-primary">{item?.variable}</code>
                    <p className="text-xs text-muted-foreground mt-0.5">{item?.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="bg-muted/50 rounded-lg border border-border p-4">
      <div className="flex items-center space-x-2 mb-3">
        <Icon name="Code" size={18} className="text-primary" />
        <h4 className="text-sm font-semibold text-foreground">Available Variables</h4>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Click any variable to insert it into your template
      </p>
      <div className="grid grid-cols-1 gap-2">
        {availableVariables?.map((item, index) => (
          <button
            key={index}
            onClick={() => handleVariableClick(item?.variable)}
            className="text-left px-3 py-2 bg-background hover:bg-muted border border-border transition-colors"
          >
            <code className="text-sm font-mono text-primary block">{item?.variable}</code>
            <p className="text-xs text-muted-foreground mt-1">{item?.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

export default VariablePicker;